// src/v3/ui/backtester/comparison-table.tsx

import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  SmartTable,
  type SmartTableColumnMeta,
} from "../_common";
import type { FilterClause, SortClause } from "../_deps";
import { backtesterPresets } from "./backtester-presets";
import type { BacktestResult } from "./backtester.hooks";

export function ComparisonTable({ results }: { results: BacktestResult[] }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState<FilterClause[]>([]);
  const [sort, setSort] = useState<SortClause[]>([]);
  const [activePresetId, setActivePresetId] = useState<string>("default");

  const columns: ColumnDef<BacktestResult>[] = useMemo(
    () => [
      { accessorKey: "strategy", header: "Strategy" },
      {
        accessorKey: "summary.netProfit",
        header: "Net Profit",
        meta: {
          format: "currency",
          formatOptions: { decimals: 2 },
        } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "summary.totalTrades",
        header: "Trades",
        meta: { format: "number" } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "summary.winRate",
        header: "Win Rate",
        meta: {
          format: "percent",
          formatOptions: { decimals: 1 },
        } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "summary.sharpe",
        header: "Sharpe",
        meta: {
          format: "number",
          formatOptions: { decimals: 2 },
        } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "summary.maxDrawdown",
        header: "Max DD",
        meta: {
          format: "percent",
          formatOptions: { decimals: 1 },
        } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "summary.profitFactor",
        header: "PF",
        meta: {
          format: "number",
          formatOptions: { decimals: 2 },
        } satisfies SmartTableColumnMeta,
      },
    ],
    [],
  );

  if (results.length < 2) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Comparison</CardTitle>
      </CardHeader>
      <CardContent>
        <SmartTable
          columns={columns}
          data={results}
          total={results.length}
          page={page}
          pageSize={pageSize}
          isLoading={false}
          error={null}
          filters={filters}
          sort={sort}
          presets={backtesterPresets}
          activePresetId={activePresetId}
          onPresetChange={setActivePresetId}
          onPageChange={setPage}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
          onFiltersChange={(f) => {
            setFilters(f);
            setPage(1);
          }}
          onSortChange={setSort}
        />
      </CardContent>
    </Card>
  );
}
